import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, SearchX } from 'lucide-react';
import { Alert, Button, EmptyState, Logo, PageHeader } from '@/components/common';
import { useGstAccount } from '@/hooks/useGst';
import type { InvoiceParty, TaxInvoice } from '@/types/gst';
import { comingSoon } from '@/utils/orderPresentation';
import { downloadInvoice, formatInvoiceAmount, GST_PATH, rupeesInWords } from '@/utils/gst';
import { formatDay } from '@/utils/productCases';

const COLUMN = 'tw-mx-auto tw-flex tw-w-full tw-max-w-[720px] tw-flex-col tw-gap-5 md:tw-gap-6';

const Party: React.FC<{ label: string; party: InvoiceParty & { placeOfSupply?: string } }> = ({ label, party }) => (
  <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
    <p className="tw-text-[12px] tw-leading-4 tw-text-ds-text-secondary">{label}</p>
    <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{party.name}</p>
    <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{party.address}</p>
    {(party.gstin || party.placeOfSupply) && (
      <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">
        {[party.gstin && `GSTIN ${party.gstin}`, party.placeOfSupply && `Place of supply: ${party.placeOfSupply}`].filter(Boolean).join(' · ')}
      </p>
    )}
  </div>
);

const AmountRow: React.FC<{ label: string; value: string; total?: boolean }> = ({ label, value, total }) => (
  <div className={total ? 'tw-flex tw-items-center tw-justify-between tw-gap-4 tw-pt-4' : 'tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-3'}>
    <dt className={total ? 'tw-text-[16px] tw-font-semibold tw-leading-6 tw-text-ds-text-primary' : 'tw-text-[14px] tw-leading-5 tw-text-ds-text-secondary'}>{label}</dt>
    <dd className={total ? 'tw-text-[17px] tw-font-bold tw-leading-6 tw-text-ds-text-primary' : 'tw-text-[14px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary'}>
      {value}
    </dd>
  </div>
);

const InvoiceDocument: React.FC<{ invoice: TaxInvoice }> = ({ invoice }) => (
  <article aria-label={`Tax invoice ${invoice.number}`} className="tw-flex tw-flex-col tw-gap-4 tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-ds-border-default tw-pb-4">
      <Logo size="sm" />
      <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-leading-4 tw-tracking-[0.06em] tw-text-ds-text-secondary">Tax invoice</p>
    </div>
    <div className="tw-grid tw-gap-4 tw-border-b tw-border-ds-border-default tw-pb-4 md:tw-grid-cols-2 md:tw-gap-6">
      <Party label="From" party={invoice.seller} />
      <Party label="Bill to" party={invoice.buyer} />
    </div>
    <div className="tw-flex tw-flex-col tw-gap-1 tw-border-b tw-border-ds-border-default tw-pb-4">
      <p className="tw-text-[12px] tw-leading-4 tw-text-ds-text-secondary">Item</p>
      <p className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{invoice.itemTitle}</p>
      {invoice.itemDetail && <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{invoice.itemDetail}</p>}
    </div>
    <dl className="tw-flex tw-flex-col">
      <AmountRow label="Taxable value" value={formatInvoiceAmount(invoice.taxableValue)} />
      {invoice.taxes.map((tax) => (
        <AmountRow key={tax.label} label={tax.label} value={formatInvoiceAmount(tax.amount)} />
      ))}
      <div className="tw-mt-1 tw-border-t tw-border-ds-border-default">
        <AmountRow total label="Total (incl. GST)" value={formatInvoiceAmount(invoice.total)} />
      </div>
    </dl>
    <p className="tw-text-[12px] tw-leading-4 tw-text-ds-text-secondary">
      Amount in words: {rupeesInWords(invoice.total)}.{invoice.paidVia && ` Paid via ${invoice.paidVia} on ${formatDay(invoice.issuedAt)}.`}
    </p>
  </article>
);

/** G5 Tax invoice: a preview of one GST invoice, with download. */
const TaxInvoicePage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { account, loading, error, reload } = useGstAccount();
  const invoice = account?.invoices.find((i) => i.id === invoiceId);
  const back = { to: GST_PATH, label: 'GST invoices' };

  if (loading || error || !invoice) {
    return (
      <div className={COLUMN}>
        <PageHeader title="Tax invoice" back={back} />
        {loading ? (
          <div aria-busy className="tw-h-96 tw-animate-pulse tw-rounded-2xl tw-bg-ds-surface" />
        ) : error ? (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Alert tone="danger" title="Something went wrong">
              {error}
            </Alert>
            <Button variant="secondary" className="tw-self-start" onClick={reload}>
              Try again
            </Button>
          </div>
        ) : (
          <EmptyState
            icon={SearchX}
            title="Invoice not found"
            description="This invoice isn’t linked to your account."
            action={<Button onClick={() => navigate(GST_PATH)}>Back to GST invoices</Button>}
          />
        )}
      </div>
    );
  }

  return (
    <div className={COLUMN}>
      <PageHeader
        title="Tax invoice"
        subtitle={`${invoice.number} · ${formatDay(invoice.issuedAt)}`}
        back={back}
        actions={
          <Button size="sm" leadingIcon={Download} onClick={() => downloadInvoice(invoice)}>
            Download PDF
          </Button>
        }
      />
      <InvoiceDocument invoice={invoice} />
      <div className="tw-flex tw-flex-col tw-gap-2.5 md:tw-hidden">
        <Button fullWidth leadingIcon={Download} onClick={() => downloadInvoice(invoice)}>
          Download PDF
        </Button>
        <Button fullWidth variant="secondary" onClick={() => comingSoon('Sharing invoices')}>
          Share
        </Button>
      </div>
    </div>
  );
};

export default TaxInvoicePage;
